import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Target,
  BookOpen,
  CheckCircle2,
  TrendingUp,
  Users,
  FileCheck,
  BarChart3,
  GraduationCap,
  Lightbulb,
  Building2,
  ChevronRight,
  Star,
  Award,
  Brain,
  LineChart,
  Shield,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

export const OBEImplementation = () => {
  const navigate = useNavigate();

  const pageData = {
    title: 'Outcome Based Education in India',
    subtitle: 'OBE Implementation & Training for Higher Education Institutions',
    description: 'Comprehensive guidance on Outcome Based Education in India covering OBE framework, assessment, mapping & accreditation requirements.',
    meta_title: 'Outcome Based Education in India | OBE Implementation & Training',
    meta_description: 'Comprehensive guidance on Outcome Based Education in India covering OBE framework, assessment, mapping & accreditation requirements.',
    meta_keywords: 'outcome based education in india, OBE implementation, OBE training, NBA accreditation, NAAC OBE, CO-PO mapping, Bloom\'s taxonomy, OBE framework india, outcome based education NBA, NAAC outcome based education',
    canonicalUrl: 'https://isde.academy/training/outcome-based-education-in-india'
  };

  const trainingModules = [
    {
      title: 'OBE Framework & NBA SAR 2025',
      icon: Award,
      duration: '5 Hours',
      topics: [
        'Understanding Outcome-Based Education Philosophy',
        'NBA SAR 2025 New Format and GAPC v4 Alignment',
        'Four Interconnected Layers: PEOs, POs, PSOs, COs',
        'Changes from NBA 2016 to NBA 2024 Framework',
        'Mandatory Effective Date: January 1, 2025',
        'Data Validation and Evidence Requirements',
        'Sustainability and Ethics Focus in New POs'
      ],
      outcomes: [
        'Complete understanding of OBE philosophy and NBA 2025 requirements',
        'Clarity on GAPC v4 alignment and new framework',
        'Readiness for Tier-I and Tier-II accreditation'
      ]
    },
    {
      title: 'Bloom\'s Taxonomy & Learning Outcomes',
      icon: Brain,
      duration: '4 Hours',
      topics: [
        'Six Cognitive Levels: Remembering to Creating',
        'Action Verbs for Each Cognitive Level',
        'Writing Measurable Course Outcomes (COs)',
        'Aligning Assessments with Cognitive Levels',
        'Higher-Order Thinking Skills (HOTS) Integration',
        'Knowledge Dimension vs Cognitive Process',
        'Avoiding Rote Learning in Curriculum Design'
      ],
      outcomes: [
        'Expertise in writing COs using Bloom\'s taxonomy',
        'Ability to design assessments for all cognitive levels',
        'Focus on higher-order thinking skills development'
      ]
    },
    {
      title: 'CO-PO Mapping & PSO Formulation',
      icon: Target,
      duration: '5 Hours',
      topics: [
        'Understanding 12 NBA Program Outcomes',
        'Formulating Program Specific Outcomes (PSOs)',
        'CO-PO Mapping Using Strength Scale (1-2-3)',
        'Direct and Indirect Assessment Methods',
        'Correlation Between COs and Graduate Attributes',
        'Documentation and Evidence Preparation',
        'Best Practices for Strong Mapping'
      ],
      outcomes: [
        'Mastery of CO-PO mapping methodology',
        'Ability to formulate discipline-specific PSOs',
        'Strong documentation for NBA submission'
      ]
    },
    {
      title: 'Attainment Calculation & Analysis',
      icon: BarChart3,
      duration: '6 Hours',
      topics: [
        'Setting Performance Targets (60-70% Threshold)',
        'Direct Attainment: Internal and External Assessments',
        'Indirect Attainment: Exit Surveys and Feedback',
        'Weighted Average Calculation (90% Direct + 10% Indirect)',
        'CO Attainment Aggregation to PO Attainment',
        'Multi-Year Trend Analysis',
        'Gap Identification and Action Plans',
        'Attainment Reporting for NBA'
      ],
      outcomes: [
        'Complete understanding of attainment calculation',
        'Ability to analyze and interpret attainment data',
        'Strategic action planning for continuous improvement'
      ]
    },
    {
      title: 'Assessment Design & Tools',
      icon: FileCheck,
      duration: '5 Hours',
      topics: [
        'Formative vs Summative Assessment Strategies',
        'Rubric Design for Outcome Measurement',
        'Bloom\'s Taxonomy-Based Question Papers',
        'Performance-Based Assessment Methods',
        'Project and Lab Assessment Techniques',
        'Authentic Assessment for Real-World Skills',
        'Technology-Enabled Assessment Tools',
        'Assessment Validation and Moderation'
      ],
      outcomes: [
        'Expertise in designing OBE-aligned assessments',
        'Ability to create effective rubrics and tools',
        'Comprehensive assessment validation skills'
      ]
    },
    {
      title: 'Continuous Improvement & Quality Enhancement',
      icon: TrendingUp,
      duration: '4 Hours',
      topics: [
        'PDCA Cycle for OBE Implementation',
        'Analyzing Multi-Year Attainment Trends',
        'Stakeholder Feedback Integration',
        'Curriculum Revision Based on Gap Analysis',
        'Faculty Development for OBE',
        'Measurable Improvement Demonstration',
        'Best Practices from High-Performing Institutions',
        'Industry Alignment and Employability Enhancement'
      ],
      outcomes: [
        'Systematic continuous improvement framework',
        'Data-driven decision making for quality enhancement',
        'Proven strategies for 28% employability increase'
      ]
    }
  ];

  const whyChoose = [
    {
      title: 'NBA 2025 Ready',
      description: 'Latest training aligned with SAR 2025 format and GAPC v4 framework',
      icon: TrendingUp,
      color: 'emerald'
    },
    {
      title: 'Proven Results',
      description: '28% increase in graduate employability reported by OBE-led institutions',
      icon: Star,
      color: 'blue'
    },
    {
      title: 'Practical Approach',
      description: 'Hands-on CO-PO mapping, attainment calculation, and assessment design',
      icon: Lightbulb,
      color: 'purple'
    },
    {
      title: 'Expert Faculty',
      description: 'Trainers with NBA peer team and OBE implementation experience',
      icon: GraduationCap,
      color: 'orange'
    }
  ];

  const keyBenefits = [
    {
      title: 'For Institutions',
      icon: Building2,
      benefits: [
        'Successful NBA Tier-I and Tier-II accreditation',
        'Enhanced curriculum quality and learning outcomes',
        'Improved student employability (28% average increase)',
        'Systematic quality assurance processes',
        'Better industry alignment and collaboration',
        'Competitive advantage in higher education landscape'
      ]
    },
    {
      title: 'For Faculty & Coordinators',
      icon: GraduationCap,
      benefits: [
        'Complete mastery of OBE framework and implementation',
        'Confidence in CO-PO mapping and attainment calculation',
        'Expertise in Bloom\'s taxonomy-based assessment design',
        'Ability to lead institutional OBE initiatives',
        'Professional recognition and career advancement',
        'Network with OBE experts across institutions'
      ]
    }
  ];

  const deliveryOptions = [
    {
      mode: 'On-Campus Workshop',
      duration: '3-4 Days Intensive',
      description: 'Comprehensive on-site training with hands-on CO-PO mapping, attainment calculation exercises, and curriculum design workshops for faculty teams',
      ideal: 'Best for institutions preparing for NBA accreditation in 2025-2026'
    },
    {
      mode: 'Online Live Training',
      duration: '8-10 Sessions (3 weeks)',
      description: 'Interactive online sessions with recorded access, OBE templates, attainment calculators, and post-training implementation support',
      ideal: 'Flexible option for multiple departments and faculty members'
    },
    {
      mode: 'Hybrid Consulting',
      duration: 'Customized (2-6 months)',
      description: 'End-to-end OBE implementation support including curriculum revision, CO-PO mapping, attainment system setup, faculty training, and NBA readiness',
      ideal: 'Complete solution for institutions transitioning to OBE framework'
    }
  ];

  const targetAudience = [
    'NBA Coordinators and Accreditation Teams',
    'Department Heads and Program Chairs',
    'Faculty Members and Course Coordinators',
    'Curriculum Development Committees',
    'IQAC Members and Quality Assurance Officers',
    'Academic Deans and Vice-Principals',
    'Assessment and Evaluation Coordinators'
  ];

  return (
    <>
      <Helmet>
        <title>{pageData.meta_title}</title>
        <meta name="description" content={pageData.meta_description} />
        <meta name="keywords" content={pageData.meta_keywords} />
        <link rel="canonical" href={pageData.canonicalUrl} />
        <meta property="og:title" content={pageData.meta_title} />
        <meta property="og:description" content={pageData.meta_description} />
        <meta property="og:url" content={pageData.canonicalUrl} />
        <meta name="twitter:title" content={pageData.meta_title} />
        <meta name="twitter:description" content={pageData.meta_description} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white py-24 mt-20">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 -left-4 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob"></div>
            <div className="absolute top-0 -right-4 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob animation-delay-2000"></div>
          </div>

          <div className="absolute top-10 right-10 opacity-20">
            <Target className="h-32 w-32" />
          </div>

          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.div
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                <Award className="h-5 w-5 text-yellow-200" />
                <span className="text-sm font-semibold">NBA & NAAC Accreditation Ready</span>
              </motion.div>

              <h1 className="text-4xl md:text-6xl font-bold mb-6">{pageData.title}</h1>
              <p className="text-xl md:text-2xl text-blue-100 mb-4">{pageData.subtitle}</p>
              <p className="text-lg text-blue-50 mb-8 max-w-3xl mx-auto">
                Outcome Based Education (OBE) has become a cornerstone of modern education systems across the globe, and is now a mandatory academic framework in India driven by regulatory bodies such as NBA and NAAC.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/contact')}
                  className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg font-semibold"
                >
                  Request Training
                </Button>
                <Button
                  onClick={() => navigate('/training-calendar')}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-6 text-lg font-semibold"
                >
                  View Schedule
                </Button>
              </div>
            </motion.div>
          </div>

          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 120" fill="none">
              <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F9FAFB"/>
            </svg>
          </div>
        </section>

        {/* What is OBE - H2 */}
        <section className="max-w-7xl mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                What Is Outcome Based Education?
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Outcome-Based Education is an educational approach where learning outcomes drive curriculum design, teaching strategies, assessment methods, and evaluation processes. Instead of focusing on what is taught, OBE emphasizes what learners are expected to achieve.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                OBE shifts the focus from traditional teaching methods to clearly defined learning outcomes, ensuring students acquire measurable knowledge, skills, and competencies by the end of a program.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Why OBE is Critical in India - H2 */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Outcome-Based Education Is Critical in India
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                The Indian higher education ecosystem is undergoing a massive transformation
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mb-12">
              {[
                {
                  title: 'NEP 2020 Implementation',
                  icon: BookOpen,
                  description: 'National Education Policy 2020 mandates outcome-based learning frameworks across all higher education institutions.',
                  color: 'text-blue-600',
                  bgColor: 'bg-blue-50'
                },
                {
                  title: 'Employability Focus',
                  icon: TrendingUp,
                  description: 'Increased focus on employability and industry-relevant skills through measurable learning outcomes.',
                  color: 'text-emerald-600',
                  bgColor: 'bg-emerald-50'
                },
                {
                  title: 'Global Benchmarking',
                  icon: Target,
                  description: 'Global benchmarking of academic programs against international quality standards.',
                  color: 'text-purple-600',
                  bgColor: 'bg-purple-50'
                },
                {
                  title: 'Accreditation-Driven QA',
                  icon: Award,
                  description: 'Accreditation-driven quality assurance through NBA and NAAC frameworks.',
                  color: 'text-orange-600',
                  bgColor: 'bg-orange-50'
                }
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="h-full hover:shadow-xl transition-all duration-300">
                      <CardContent className="p-6">
                        <div className={`${item.bgColor} w-14 h-14 rounded-xl flex items-center justify-center mb-4`}>
                          <Icon className={`h-7 w-7 ${item.color}`} />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                        <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>

            {/* Regulatory Drivers */}
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Regulatory Drivers</h3>
                <div className="space-y-4">
                  <div className="p-5 bg-blue-50 rounded-lg border-l-4 border-blue-600">
                    <p className="text-gray-700 leading-relaxed">
                      <span className="font-bold text-gray-900">Outcome Based Education NBA</span> is mandatory for engineering and technical programs seeking NBA accreditation.
                    </p>
                  </div>
                  <div className="p-5 bg-emerald-50 rounded-lg border-l-4 border-emerald-600">
                    <p className="text-gray-700 leading-relaxed">
                      <span className="font-bold text-gray-900">NAAC Outcome Based Education</span> framework evaluates institutions on outcome attainment, student performance, and academic effectiveness.
                    </p>
                  </div>
                  <div className="p-5 bg-purple-50 rounded-lg border-l-4 border-purple-600">
                    <p className="text-gray-700 leading-relaxed">
                      Institutions are expected to demonstrate <span className="font-bold">documented evidence</span> of outcomes and continuous improvement.
                    </p>
                  </div>
                </div>
                <p className="text-lg text-gray-700 mt-6 leading-relaxed">
                  As a result, OBE Implementation Training in India has become essential for institutions aiming for accreditation, ranking, and academic excellence.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Comprehensive Training Modules - H2 */}
        <section className="max-w-7xl mx-auto px-4 py-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">Comprehensive OBE Training Modules</h2>
          <p className="text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Structured curriculum covering all aspects of OBE implementation from framework understanding to continuous improvement
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {trainingModules.map((module, index) => {
              const IconComponent = module.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-lg transition-all">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <IconComponent className="h-6 w-6 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold mb-1">{module.title}</h3>
                          <p className="text-sm text-blue-600 font-semibold">Duration: {module.duration}</p>
                        </div>
                      </div>

                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Key Topics:</h4>
                        <ul className="space-y-1.5">
                          {module.topics.map((topic, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                              <CheckCircle2 className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                              <span>{topic}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="bg-blue-50 p-3 rounded-lg">
                        <h4 className="text-xs font-semibold text-blue-700 uppercase mb-1">Learning Outcomes:</h4>
                        <ul className="space-y-1">
                          {module.outcomes.map((outcome, i) => (
                            <li key={i} className="text-sm text-gray-700 flex items-start gap-1.5">
                              <span className="text-blue-600">•</span>
                              {outcome}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* OBE NBA - H3 */}
        <section className="bg-white py-16">
          <div className="max-w-4xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                Outcome Based Education NBA – Importance for Engineering Colleges
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Outcome Based Education NBA is a mandatory requirement for engineering and technical institutions seeking NBA accreditation in India.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">NBA evaluates:</p>
              <div className="grid md:grid-cols-2 gap-4 mb-8">
                {[
                  'Outcome formulation and relevance',
                  'Outcome attainment levels',
                  'Evidence-based assessment',
                  'Continuous improvement initiatives'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>

              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Institutions that fail to demonstrate robust OBE implementation risk:
              </p>
              <div className="space-y-3 mb-6">
                {[
                  'Accreditation denial',
                  'Lower academic credibility',
                  'Reduced industry acceptance'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-red-50 rounded-lg border-l-4 border-red-400">
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
                <p className="text-lg text-gray-700 leading-relaxed font-medium">
                  OBE Implementation Training ensures that institutions fully comply with NBA's outcome-based accreditation model.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* NAAC OBE Webinar - H3 */}
        <section className="bg-gradient-to-b from-gray-50 to-white py-16">
          <div className="max-w-4xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                NAAC Outcome Based Education Webinar – A Key Awareness Tool
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Many institutions participate in NAAC Outcome Based Education webinars to understand evolving accreditation requirements.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">These webinars focus on:</p>
              <div className="grid md:grid-cols-2 gap-4 mb-8">
                {[
                  'NAAC OBE metrics and indicators',
                  'Outcome-based teaching-learning practices',
                  'Student-centric outcome evaluation',
                  'Best practices for NAAC documentation'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-emerald-50 rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>

              <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-6 rounded-lg border border-emerald-200">
                <p className="text-lg text-gray-700 leading-relaxed">
                  However, while webinars provide awareness, <span className="font-bold">hands-on OBE implementation training</span> is essential for practical execution and sustained compliance.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Why Choose Section */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Why Choose Our OBE Training?</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whyChoose.map((item, index) => {
                const IconComponent = item.icon;
                const colors = {
                  emerald: 'bg-emerald-50 text-emerald-600',
                  blue: 'bg-blue-50 text-blue-600',
                  purple: 'bg-purple-50 text-purple-600',
                  orange: 'bg-orange-50 text-orange-600'
                };

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="h-full text-center hover:shadow-lg transition-all">
                      <CardContent className="p-6">
                        <div className={`${colors[item.color]} w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4`}>
                          <IconComponent className="h-6 w-6" />
                        </div>
                        <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                        <p className="text-sm text-gray-600">{item.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Key Benefits */}
        <section className="max-w-7xl mx-auto px-4 py-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Key Benefits</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {keyBenefits.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                >
                  <Card className="h-full">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <IconComponent className="h-6 w-6 text-blue-600" />
                        </div>
                        <h3 className="text-xl font-bold">{item.title}</h3>
                      </div>
                      <ul className="space-y-2.5">
                        {item.benefits.map((benefit, i) => (
                          <li key={i} className="flex items-start gap-2.5">
                            <ChevronRight className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Delivery Options */}
        <section className="bg-gradient-to-br from-gray-50 to-blue-50 py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">Training Delivery Options</h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              Choose the format that best suits your institution's needs and timeline
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              {deliveryOptions.map((option, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-xl transition-all hover:-translate-y-1">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold mb-2 text-blue-600">{option.mode}</h3>
                      <p className="text-sm font-semibold text-gray-700 mb-3">{option.duration}</p>
                      <p className="text-gray-600 mb-4">{option.description}</p>
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <p className="text-xs font-semibold text-blue-700 uppercase mb-1">Ideal For:</p>
                        <p className="text-sm text-gray-700">{option.ideal}</p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Target Audience */}
        <section className="max-w-7xl mx-auto px-4 py-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">Who Should Attend?</h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            This training is essential for all stakeholders involved in OBE implementation and NBA accreditation
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {targetAudience.map((audience, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm border border-blue-100 hover:shadow-md transition-all"
              >
                <Users className="h-5 w-5 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{audience}</span>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Ready to Implement World-Class OBE Framework?
              </h2>
              <p className="text-xl text-blue-50 mb-8">
                Join institutions achieving 28% higher employability and successful NBA accreditation with our expert OBE training
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/contact')}
                  className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg font-semibold"
                >
                  Schedule Training
                </Button>
                <Button
                  onClick={() => navigate('/training/hei-training')}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-6 text-lg font-semibold"
                >
                  View All HEI Programs
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default OBEImplementation;
